// pages/resize.tsx

import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "react-router-dom";
import BasicButton from "@components/button/BasicButton";
import Loading from "./Loading";
import { useMutation } from "@tanstack/react-query";
import { FILE_URL, HEALTH_API_URL, PREDICT_API_URL } from "@libs/apiUrl";
import useAxios from "@libs/axios/useAxios";
import { useImageAnalysisStore } from "@store/imageAnalysis/useImageAnalysisStore";
import BackHeader from "@components/header/BackHeader";
import BasicModal from "@components/modal/BasicModal";
import useModalStore from "@store/modalStore";
import { ModalContent } from "@components/modal/ModalContent";
import axios from "axios";
import ToastAlert from "@components/alert/ToastAlert";
import { useApiRequest } from "@utils/ApiRequest";

const Resize = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { axiosInstance } = useAxios();
  const { getColorSuggestions } = useApiRequest();
  const { setUploadedImageUrl, setAiAnalysisResult, setColorSuggestions } = useImageAnalysisStore();
  const { modals, closeModal } = useModalStore();

  // console.log("location.state 전체:", location.state);

  const analyzedImageFile = location.state?.imageFile; // File 객체
  const imageUrl = location.state?.image;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // 파일 업로드 Mutation
  const { mutate: uploadFileMutate, isPending: isUploadPending } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("files", file);
      // 실패 시 base64(data URL)로 대신 진행하지 않는다. 이미지 원본이
      // DB에 쌓여 운영 DB가 멈춘 적이 있다(2026-10-07).
      return axiosInstance.post(FILE_URL, formData);
    },
    onSuccess: async (response, file) => {
      const uploadedUrl = response.data.data?.[0]?.url;
      if (!uploadedUrl) {
        ToastAlert("이미지 업로드에 실패했습니다.\n다시 시도해주세요.");
        return;
      }
      setUploadedImageUrl(uploadedUrl);

      // 분류 모델 시도 → 실패 시 스킵 (CaptureDetail에서 LLM fallback)
      try {
        const formData = new FormData();
        formData.append("image", file);
        const aiResponse = await axios.post(PREDICT_API_URL, formData);
        // 배포환경에서 프록시 미설정 시 HTML 반환 방어
        if (typeof aiResponse.data === 'string' || !aiResponse.data?.clothing_classification) {
          throw new Error('분류모델 응답이 유효하지 않습니다');
        }
        setAiAnalysisResult(aiResponse.data, new Date().toISOString());
        console.log("[분류모델] 성공:", aiResponse.data);
      } catch (err) {
        console.warn("[분류모델] 실패 → LLM fallback 전환:", err);
        // 빈 분석 결과 + 타임스탬프만 세팅
        setAiAnalysisResult(
          {
            success: false,
            segmentation_applied: false,
            clothing_classification: { predictions: [] },
            color_classification: { id: 0, is_multi: false },
            image_path: "",
            pattern_classification: { predictions: [] },
          },
          new Date().toISOString()
        );
      }

      try {
        setColorSuggestions(await getColorSuggestions(file));
      } catch (error) {
        console.warn("[대표 색상 추천] 실패:", error);
        setColorSuggestions([]);
      }

      navigate("/capture-detail", { replace: true });
    },
    onError: (error) => {
      console.error("파일 업로드 실패:", error);
      ToastAlert("이미지 업로드에 실패했습니다.\n다시 시도해주세요.");
    },
  });

  const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: any,
  ): Promise<Blob> => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error("이미지 로드에 실패했습니다."));
    });

    const canvas = document.createElement("canvas");
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas context not available");

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("이미지 변환에 실패했습니다."));
        }
      }, "image/jpeg");
    });
  };

  // 옷 분류하기 버튼 클릭 시 실행
  const handleClassify = async () => {
    try {
      // 0단계: 분류 모델 헬스체크 (실패해도 진행)
      try {
        await axios.get(HEALTH_API_URL);
        console.log("[분류모델] 헬스체크 OK");
      } catch {
        console.warn("[분류모델] 헬스체크 실패 → LLM fallback으로 진행");
      }

      // 1단계: 이미지 자르기
      const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], analyzedImageFile.name, {
        type: "image/jpeg",
      });

      // 2단계: 파일 업로드 → onSuccess에서 분류 모델 시도 + navigate
      uploadFileMutate(croppedFile);
    } catch (err) {
      console.error("에러 발생:", err);
      ToastAlert(
        "이미지 분석에 실패하였습니다.\n다시 시도하거나 다른 이미지를 사용해주세요.",
      );
    }
  };
  const isPending = isUploadPending;

  return (
    <Container>
      <BackHeader
        title=""
        onClick={() => navigate("/capture-list", { replace: true })}
      />
      {isPending ? (
        <Loading />
      ) : (
        <>
          <CropWrapper>
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={1} // 1:1 비율
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="horizontal-cover"
              cropShape="rect"
            />
          </CropWrapper>

          <ButtonWrapper>
            <BasicButton
              title="옷 분류하기"
              onClick={handleClassify}
              width="100%"
            />
          </ButtonWrapper>
        </>
      )}

      {/* 모바일 에러 확인 테스트용 */}
      <BasicModal
        open={modals.errorModal}
        activeName="확인"
        closeName="취소"
        activeEvent={() => {
          closeModal("errorModal");
        }}
        closeModal={() => closeModal("errorModal")}>
        <ModalContent
          mainMessage="에러 발생"
          subMessage="네트워크 상태를 확인해주세요."
        />
      </BasicModal>
    </Container>
  );
};

export default Resize;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  /* width: 560px; */
  /* border: 12px solid blue; */
`;

const CropWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  /* border: 12px solid orange; */
  /* background-color: green; */
  /* aspect-ratio: 1 / 1; */
  /* max-width: 560px; */

  /* .reactEasyCrop_CropArea,
  .reactEasyCrop_CropAreaGrid {
    width: 100% !important;
    box-shadow: 0 0 0 9999em !important;
    aspect-ratio: 1 / 1 !important;
    border: 2px solid blue;
  } */
`;

const ButtonWrapper = styled.div`
  padding: 1rem;
  background-color: white;
  z-index: 10;
  width: 100%;
`;
